import type { NativeTableSnapshot } from "../common/components/types";
import type { DocumentDataWithID, Query } from "../common/Database";
import {
    makeAppUserIDQuery,
    makePrivateRowsQuery,
    makeRowVersionQuery,
    makeTableDataFromDocument,
} from "../common/Database";
import {
    documentIDForTableName,
    makePrivateRowsPath,
    makePublicRowsPath,
    makeTablesPath,
    makeUserRowsPath,
} from "../common/database-strings";
import { logInfo } from "../common/debug-print";
import type { TableGlideType } from "../common/description";
import { getTableName, tableHasRowOwners } from "../common/description";
import { assert, defined, dontAwait, panic } from "../support";
import type { DurableStorageController } from "./durable-storage-controller";
import type { DocumentsFirestoreHandler } from "./types";

// WaitingForSnapshot: The initial state.  The table isn't being listened to,
//   so we're waiting for either the snapshot response to arrive or the table
//   being listened to.
//
// WaitingToFetchRows: We're still waiting for the snapshot response, but the
//   table is now being listened to.
//
// GotSnapshotResponse: We have the snapshot response, but the table is not
//   being listened to.
//
// Listening: We have the snapshot response, and the table is being listened
//   to.

// Transitions:
//
// WaitingForSnapshot:
//   snapshot is loaded -> GotSnapshotResponse
//   snapshot is unavailable -> GotSnapshotResponse
//   fetch requested -> WaitingToFetchRows
// WaitingToFetchRows:
//   snapshot is loaded -> Listening
//   snapshot is unavailable -> Listening
//   fetch requested -> WaitingToFetchRows
// GotSnapshotResponse:
//   fetch requested -> Listening
// Listening:
//   fetch requested -> Listening

enum State {
    WaitingForSnapshot,
    WaitingToFetchRows,
    GotSnapshotResponse,
    Listening,
}

export abstract class FirestoreTableListener {
    protected state: State;

    private _unlistens: (() => void)[] = [];
    private _userSpecificUnlistens: (() => void)[] | undefined;

    constructor(
        protected readonly handler: DocumentsFirestoreHandler,
        public readonly table: TableGlideType,
        protected readonly isBuilder: boolean,
        private readonly _durableStorageController: DurableStorageController
    ) {
        this.state = State.WaitingForSnapshot;
    }

    protected abstract updateWithSnapshot(force: boolean): void;

    // These are called by the handler when the app data snapshot is loaded,
    // or couldn't be loaded.
    public abstract snapshotLoaded(version: number, documents: readonly DocumentDataWithID[] | undefined): void;
    public abstract snapshotUnavailable(): void;

    protected abstract getSnapshotDataVersion(): number | undefined;

    // This is called the first time we transition to `WaitingToFetchRows`.
    protected async waitingToFetchRows(): Promise<void> {
        return;
    }

    public listenToTable(resendSnapshot: boolean): void {
        if (resendSnapshot) {
            this.updateWithSnapshot(true);
        }

        switch (this.state) {
            case State.WaitingForSnapshot:
                this.state = State.WaitingToFetchRows;
                void this.waitingToFetchRows?.();
                break;

            case State.GotSnapshotResponse:
                this.state = State.Listening;
                this.startListening(true);
                break;

            case State.WaitingToFetchRows:
                break;

            case State.Listening:
                this.startListening(false);
                break;

            default:
                return panic("Unexpected transition");
        }
    }

    protected gotSnapshotResponse(gotSnapshot: boolean): void {
        if (gotSnapshot) {
            this.updateWithSnapshot(false);
        }

        switch (this.state) {
            case State.WaitingForSnapshot:
                this.state = State.GotSnapshotResponse;
                break;

            case State.WaitingToFetchRows:
                this.state = State.Listening;
                this.startListening(true);
                break;

            default:
                return panic("Unexpected transition");
        }
    }

    // Private rows don't use snapshots, so the row version is not included in
    // the query.
    private getQueriesForPrivateRows(): readonly Query[] {
        if (this.isBuilder) {
            return [];
        } else {
            return [makePrivateRowsQuery(defined(this.handler.appUserID), this.handler.appUserRoles)];
        }
    }

    protected startListening(firstListen: boolean): void {
        assert(this.state === State.Listening);

        const tableName = getTableName(this.table);
        const documentID = documentIDForTableName(tableName);
        const tablesPath = makeTablesPath(this.handler.appID);
        const { database } = this.handler;

        // logInfo("listening to full", tableName.name, firstListen);

        // We don't sort by row index because that would leave out documents that don't
        // have a row index set yet.
        // FIXME: This should never be the case that documents don't have row indexes.
        // We can safely sort now.

        // Rows from tables may take a while to load on the first listen.
        // We want to establish a network connection lock, but only for the
        // initial listen.
        function withInitialTableNetworkLock(fn: (unlock: () => void) => () => void): () => void {
            let tableNetworkLock: Promise<void> | undefined = database.enterNetworkConnectionLock();

            const exitLock = () => {
                if (tableNetworkLock !== undefined) {
                    dontAwait(tableNetworkLock.then(() => database.exitNetworkConnectionLock()));
                    tableNetworkLock = undefined;
                }
            };

            const unlisten = fn(exitLock);
            return () => {
                exitLock();
                unlisten();
            };
        }

        if (firstListen) {
            const publicPath = makePublicRowsPath(tablesPath, documentID);

            const queries: Query[] = [];
            // If the table isn't included in the snapshot (that's the case
            // for native tables) then we don't query for row version.
            const snapshotDataVersion = this.getSnapshotDataVersion();
            if (snapshotDataVersion !== undefined) {
                queries.push(makeRowVersionQuery(snapshotDataVersion));
            }
            logInfo("querying", this.table, queries);

            // It's possible that this query fails due to missing permissions.
            // In that case we want to treat it as if no rows were loaded, hence
            // the `onError` handler.  If we didn't do that, the user profile
            // row might forever be in a loading state, which would lead to
            // ##tabVisibility issues.

            const publicUnlisten = withInitialTableNetworkLock(unlock =>
                database.listenDiffWhere(
                    publicPath,
                    queries,
                    undefined,
                    undefined,
                    results => {
                        try {
                            this.handler.updateWithDiffResults(tableName, results, false);
                        } finally {
                            unlock();
                        }
                    },
                    () => {
                        try {
                            this.handler.updateWithDiffResults(tableName, [], false);
                        } finally {
                            unlock();
                        }
                    }
                )
            );

            this._unlistens.push(publicUnlisten);

            // Table data is a single document so we don't need to establish a
            // network lock for it. It should be fast enough to pick up.
            const tableDataUnlisten = database.listenToDocument(tablesPath, documentID, doc => {
                const data = makeTableDataFromDocument(doc);
                // Old table documents don't have version numbers.  For those
                // we assume that they're the latest ones vs any row versions,
                // therefore the max integer.
                const version = data.version ?? Number.MAX_SAFE_INTEGER;
                this.handler.setDeletedRowIndexes(tableName, data.deletedRowIndexes, version);
                for (const rowIndex of data.deletedRowIndexes) {
                    this._durableStorageController.handleDocumentDeletion(tableName, rowIndex, version);
                }
            });
            this._unlistens.push(tableDataUnlisten);
        }

        // When we're in the builder it's possible that we don't have an app
        // user ID (yet), but we must still load the private rows.  This is
        // particularly the case when we're support or admin, in which case
        // the backend might not give us an app user ID.
        if (this._userSpecificUnlistens === undefined && (this.handler.appUserID !== undefined || this.isBuilder)) {
            this._userSpecificUnlistens = [];
            if (tableHasRowOwners(this.table) || this.isBuilder) {
                const queries = this.getQueriesForPrivateRows();
                logInfo("listening to private rows", tableName.name, this.handler.appUserID, queries);

                const privatePath = makePrivateRowsPath(tablesPath, documentID);
                const privateUnlisten = withInitialTableNetworkLock(unlock =>
                    database.listenDiffWhere(
                        privatePath,
                        queries,
                        undefined,
                        undefined,
                        results => {
                            try {
                                this.handler.updateWithDiffResults(tableName, results, true);
                            } finally {
                                unlock();
                            }
                        },
                        () => {
                            try {
                                this.handler.updateWithDiffResults(tableName, [], true);
                            } finally {
                                unlock();
                            }
                        }
                    )
                );

                this._userSpecificUnlistens.push(privateUnlisten);
            }

            logInfo("listening to user-specific columns", tableName.name, this.handler.appUserID);

            if (this.handler.appUserID !== undefined) {
                const userSpecificUnlisten = withInitialTableNetworkLock(unlock =>
                    database.listenWhere(
                        makeUserRowsPath(makeTablesPath(this.handler.appID), tableName),
                        [makeAppUserIDQuery(defined(this.handler.appUserID))],
                        undefined,
                        undefined,
                        undefined,
                        results => {
                            try {
                                this.handler.updateWithUserSpecificColumns(this.table, results);
                            } finally {
                                unlock();
                            }
                        },
                        () => unlock()
                    )
                );

                this._userSpecificUnlistens.push(userSpecificUnlisten);
            }
        }
    }

    public unlisten(): void {
        if (this.state !== State.Listening) return;

        this.unlistenUserSpecifics();

        for (const unlisten of this._unlistens) {
            unlisten();
        }
        this._unlistens = [];

        this.state = State.GotSnapshotResponse;
    }

    public unlistenUserSpecifics(): void {
        if (this.state !== State.Listening) return;
        if (this._userSpecificUnlistens === undefined) return;

        for (const unlisten of this._userSpecificUnlistens) {
            unlisten();
        }
        this._userSpecificUnlistens = undefined;
    }

    public resetFromFirestore(): void {
        this.unlisten();
        this.listenToTable(true);
    }
}

export class FirestoreGoogleSheetsTableListener extends FirestoreTableListener {
    private _snapshot:
        | { version: number; documents: readonly DocumentDataWithID[] | undefined; gotDocuments: boolean }
        | undefined;

    protected getSnapshotDataVersion(): number | undefined {
        if (this._snapshot?.documents === undefined) return undefined;
        return this._snapshot.version;
    }

    protected updateWithSnapshot(force: boolean): void {
        if (this._snapshot === undefined) return;
        if (tableHasRowOwners(this.table)) return;

        // We don't update for tables with owners because we might have an old snapshot
        // where the table didn't have owners.
        this.handler.updateWithSnapshotResults(
            this.table,
            this._snapshot.documents ?? [],
            this._snapshot.version,
            force
        );
    }

    public snapshotLoaded(version: number, documents: readonly DocumentDataWithID[] | undefined): void {
        logInfo("snapshot loaded", version);

        assert(this._snapshot === undefined);
        this._snapshot = { version, documents, gotDocuments: documents !== undefined };

        this.gotSnapshotResponse(true);
    }

    public snapshotUnavailable(): void {
        logInfo("snapshot unavailable");

        this.gotSnapshotResponse(false);
    }
}

// This listener ignores the app data snapshot, and instead initiate loading
// the native table snapshot when the table is first accessed, which is the
// transition to `WaitingToFetchRows`.
export class FirestoreNativeTableListener extends FirestoreTableListener {
    // We only accept snapshots that have a version for our app.  `false`
    // means the snapshot couldn't be loaded.
    private _snapshot: NativeTableSnapshot | false | undefined;

    protected getSnapshotDataVersion(): number | undefined {
        if (this._snapshot === undefined || this._snapshot === false) return undefined;
        return defined(this._snapshot.appDataVersions[this.handler.appID]);
    }

    protected updateWithSnapshot(force: boolean): void {
        if (this._snapshot === undefined || this._snapshot === false) return;
        if (tableHasRowOwners(this.table)) return;

        // We don't update for tables with owners because we might have an old snapshot
        // where the table didn't have owners.
        this.handler.updateWithSnapshotResults(
            this.table,
            this._snapshot.rows,
            defined(this.getSnapshotDataVersion()),
            force
        );
    }

    protected async waitingToFetchRows(): Promise<void> {
        assert(this.state === State.WaitingToFetchRows);
        assert(this._snapshot === undefined);

        if (!this.isBuilder && tableHasRowOwners(this.table)) {
            this._snapshot = false;
            this.gotSnapshotResponse(false);
            return;
        }

        const snapshot = await this.handler.appFacilities.loadNativeTableSnapshot(
            this.handler.appID,
            getTableName(this.table)
        );
        assert(this._snapshot === undefined);

        if (snapshot === undefined || snapshot.appDataVersions[this.handler.appID] === undefined) {
            this._snapshot = false;
            this.gotSnapshotResponse(false);
        } else {
            logInfo("native table snapshot", snapshot);

            this._snapshot = snapshot;
            this.gotSnapshotResponse(true);
        }
    }

    public snapshotLoaded(): void {
        return;
    }

    public snapshotUnavailable(): void {
        return;
    }
}
